<template>
  <base-resize-observer @resize="onResize">
    <div data-component="layout-switcher-menu">
      <div
        ref="nav"
        class="menu-navigation"
      >
        <div
          v-for="item in visibleItems"
          :key="item.key"
          class="menu-item"
        >
          <a
            class="menu-item-link"
            :class="{ 'is-active': item.key === activeWorkspace }"
            @click.prevent="onLinkClick(item.key)"
          >
            {{ $t(item.name) }}
          </a>
        </div>

        <or-icon-button
          v-show="isMenuButtonVisible"
          ref="buttonRef"
          data-menu-trigger
          icon="more_horiz"
          @click.stop="isMenuOpen = true"
        />

        <or-menu
          v-if="isMenuButtonVisible"
          ref="menu"
          :trigger="buttonRoot"
          :is-open="isMenuOpen"
          data-menu
          @open="isMenuOpen = true"
          @close="isMenuOpen = false"
        >
          <or-menu-item
            v-for="{ key, name } in menuItems"
            :key="key"
            :selected="key === activeWorkspace"
            :data-menu-item="key"
            @click.prevent="onLinkClick(key)"
          >
            {{ name }}
          </or-menu-item>
        </or-menu>
      </div>
    </div>
  </base-resize-observer>
</template>

<script lang="ts">
import _ from 'lodash';
import {
  defineComponent,
  PropType,
  toRefs,
  getCurrentInstance,
  ref,
  computed,
  onMounted,
} from 'vue-demi';
import {
  OrMenuV3 as OrMenu,
  OrMenuItemV3 as OrMenuItem,
  OrIconButtonV3 as OrIconButton,
  OrButtonV3 as OrButton,
} from '@onereach/ui-components-25';

import { BaseResizeObserver } from '@/modules/virtual-scroll/components/decorators';
import { LayoutWorkspace } from '@/types';

export default defineComponent({
  name: 'LayoutSwitcherMenu',
  components: {
    OrIconButton,
    OrMenu,
    OrMenuItem,
    BaseResizeObserver,
  },
  props: {
    workspaces: {
      type: Object as PropType<Record<string, LayoutWorkspace>>,
      default: () => ({}),
    },
    activeWorkspace: {
      type: String,
      default: '',
    },
  },
  emits: ['link-click'],
  setup(props) {
    const { activeWorkspace, workspaces } = toRefs(props);
    const vm = getCurrentInstance()!;

    const workspaceList = computed(() => Object.values(workspaces.value));
    const GAP = 32;

    const nVisibleItems = ref(workspaceList.value.length);
    const isMenuOpen = ref(false);
    const isMenuButtonVisible = ref(false);
    const itemWidthList = ref([] as number[]);
    const buttonRef = ref<InstanceType<typeof OrButton>>();
    const buttonRoot = computed(() => buttonRef.value?.root);

    const visibleItems = computed(() => {
      return workspaceList.value.slice(0, nVisibleItems.value);
    });

    const menuItems = computed(() => {
      return Object.values(workspaces.value).slice(visibleItems.value.length);
    });

    const childrenTotalWidth = computed(() => {
      const childrenWidthSum = itemWidthList.value.reduce((result, current) => (result += current), 0);
      const gaps = itemWidthList.value.length > 1
        ? (itemWidthList.value.length - 1) * GAP
        : 0;
      return childrenWidthSum + gaps;
    });

    const onLinkClick = (key: string) => {
      if (key !== activeWorkspace.value) {
        vm.proxy.$emit('link-click', { key });
      }
    };

    const onResize = _.debounce(() => calculateMenuItems());
    const getNavElement = () => {
      return vm.proxy.$refs['nav'] as HTMLElement;
    };

    const getChildrenList = () => {
      const navElement = getNavElement();
      return Array.from(navElement.children) as HTMLElement[];
    };

    const calculateMenuItems = () => {
      const navElement = getNavElement();
      if (!navElement) {
        return;
      }

      const availableWidth = navElement.offsetWidth;
      if (childrenTotalWidth.value <= availableWidth) {
        isMenuOpen.value = false;
        isMenuButtonVisible.value = false;
        nVisibleItems.value = itemWidthList.value.length;
        return;
      }

      /* 68px? */
      const MENU_BUTTON_SPACE = 16 + 2 * 8 + GAP;
      const itemsSpace = availableWidth - MENU_BUTTON_SPACE;
      let childrenWidthSum = childrenTotalWidth.value;
      let visibleItemsCount = 0;

      for (let i = itemWidthList.value.length - 1; i >= 0; i--) {
        childrenWidthSum -= itemWidthList.value[i] + (i > 0 ? GAP : 0);
        if (childrenWidthSum <= itemsSpace && childrenWidthSum > 0) {
          visibleItemsCount = i;
          break;
        }
      }
      nVisibleItems.value = visibleItemsCount;
      isMenuButtonVisible.value = true;
    };

    /** The workspace list will stay unchanged */
    onMounted(() => {
      const childrenList = getChildrenList();
      itemWidthList.value = childrenList.map(child => child.offsetWidth);
    });

    return {
      isMenuOpen,
      isMenuButtonVisible,
      visibleItems,
      menuItems,
      buttonRef,
      buttonRoot,
      onLinkClick,
      onResize,
    };
  },
});
</script>

<style lang="scss" scoped>
@use '@/styles' as *;

[data-component='layout-switcher-menu'] {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--s-4) var(--s-6);
  background: var(--or-c-background);

  ::v-deep button {
    padding: var(--s-3);
    outline: none !important;
  }

  .menu-navigation {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--s-6);
    overflow: hidden;
    color: var(--or-c-on-surface);

    .menu-item {
      display: flex;
      max-width: 15rem;

      &-link {
        @include text-overflow();

        max-width: 100%;
        padding: var(--s-3) var(--s-4);
        font-size: 1rem;
        color: var(--or-c-on-background) !important;
        border-radius: var(--s-2);
        transition: background-color 0.3s linear;
        cursor: pointer;

        &.is-active {
          font-weight: var(--fw-2);
          cursor: default;
        }

        &:hover:not(.active) {
          background-color: var(--or-c-primary-opacity-0-08);
        }
      }
    }
  }
}
</style>
